import { compose, withState, withProps, withHandlers, lifecycle } from "recompose";
import { createVideoOrder, getVideoOrder, VideoStatus } from "../libs/videoOrder";
import getComposition from "../libs/getComposition";
import { VIDEO_TEMPLATES_URL } from "../../../constants";

export const VideoSteps = {
  CHOOSE_TEMPLATE: "choose_template",
  PREVIEW: "preview",
  RENDERING: "rendering",
  DONE: "done",
};

const withVideoSteps = compose(
  withState("videoStep", "setVideoStep", VideoSteps.CHOOSE_TEMPLATE),
  withProps(({ videoStep }) => ({
    showBackAction: videoStep !== VideoSteps.RENDERING && videoStep !== VideoSteps.DONE,
    showCloseAction: videoStep === VideoSteps.DONE,
  })),
  withHandlers({
    handlePrevStep: ({ videoStep, setVideoStep, onRequestClose }) => () => {
      switch (videoStep) {
        case VideoSteps.CHOOSE_TEMPLATE:
          if (window.gtag) window.gtag("event", "video_modal_close");
          onRequestClose();
          break;
        case VideoSteps.PREVIEW:
          if (window.gtag) window.gtag("event", "video_modal_back_choose_template");
          setVideoStep(VideoSteps.CHOOSE_TEMPLATE);
          break;
        case VideoStatus.RENDERING:
          if (window.gtag) window.gtag("event", "video_modal_back_preview");
          setVideoStep(VideoSteps.PREVIEW);
          break;
      }
    },
  })
);

const withVideoTemplates = compose(
  withState("videoTemplates", "setVideoTemplates", null),
  withState("videoTemplatesLoading", "setVideoTemplatesLoading", true),
  withState("videoTemplatesError", "setVideoTemplatesError", false),
  lifecycle({
    componentDidMount() {
      fetch(VIDEO_TEMPLATES_URL)
        .then(res => res.json())
        .then(videoTemplates => {
          this.props.setVideoTemplatesLoading(false);
          if (videoTemplates && videoTemplates.length > 0) {
            this.props.setVideoTemplates(videoTemplates);
            this.props.setCurrentVideoTemplate(videoTemplates[0]);
          } else {
            this.props.setVideoTemplatesError("No available templates");
          }
        })
        .catch(error => {
          console.warn(`Error fethcing ${VIDEO_TEMPLATES_URL}: `, error);
          this.props.setVideoTemplatesLoading(false);
          this.props.setVideoTemplatesError("Error fetching templates");
        });
    },
  })
);

const POLLING_INTERVAL = 2000;

const withRenderingVideoOrder = compose(
  withState("pollingVideoOrder", "setPollingVideoOrder", false),
  withHandlers({
    startPollingVideoOrder: props => () => {
      const { setPollingVideoOrder, setVideoUrl, videoOrderId, videoStep, setVideoStep } = props;
      const pollVideoOrder = () => {
        getVideoOrder({ id: videoOrderId }).then(videoOrder => {
          if (videoStep === VideoSteps.PREVIEW || videoStep === VideoSteps.RENDERING) {
            if (videoOrder.status === VideoStatus.DONE) {
              setPollingVideoOrder(false);
              setVideoUrl(videoOrder.videoUrl);
              setTimeout(() => {
                setVideoStep(VideoSteps.DONE);
              }, 200);
            } else if (videoOrder.status === VideoStatus.PENDING) {
              setTimeout(pollVideoOrder, POLLING_INTERVAL);
            } else {
              console.warn("VideoStatus not recognizes: ", videoOrder);
              setTimeout(pollVideoOrder, POLLING_INTERVAL);
            }
          } else {
            // User cancelled video, left VideoStep.RENDERING
            setPollingVideoOrder(false);
          }
        });
      };
      setPollingVideoOrder(true);
      pollVideoOrder();
    },
  })
);

export const withTopNineVideo = compose(
  withVideoSteps,
  withState("currentVideoTemplate", "setCurrentVideoTemplate", null),
  withVideoTemplates,
  withState("createVideoOrderLoading", "setCreateVideoOrderLoading", false),
  withState("createVideoOrderError", "setCreateVideoOrderError", null),
  withState("videoOrderId", "setVideoOrderId", null),
  withState("activeTemplateIndex", "setActiveTemplateIndex", 0),
  withState("videoUrl", "setVideoUrl", null),
  withState("loadingPurchase", "setLoadingPurchase", null),
  withRenderingVideoOrder,
  withHandlers({
    getCurrentComposition: props => () => {
      const { currentVideoTemplate, topNineSource, username, totalLikes, likesPerPost } = props;
      if (props.currentVideoTemplate) {
        const footages = topNineSource.posts;
        return getComposition({ template: currentVideoTemplate, footages, username, totalLikes, likesPerPost });
      }
      return null;
    },
  }),
  withHandlers({
    onVideoTemplateSlideTo: props => templateIndex => {
      const { setCurrentVideoTemplate, setActiveTemplateIndex, videoTemplates } = props;
      setActiveTemplateIndex(templateIndex);
      const videoTemplate = videoTemplates[templateIndex];
      setCurrentVideoTemplate(videoTemplate);
    },
    onChooseTemplate: props => () => {
      props.refetchUserPurchases();
      props.setVideoStep(VideoSteps.PREVIEW);
    },
    onBackFromPreview: props => () => {
      if (props.videoTemplates && props.videoTemplates.length > 0) {
        props.setCurrentVideoTemplate(props.videoTemplates[0]);
      } else {
        props.setCurrentVideoTemplate(null);
      }
      props.setVideoStep(VideoSteps.CHOOSE_TEMPLATE);
    },
    onCreateVideoOrder: props => ({ stripe }) => {
      const {
        currentVideoTemplate,
        getCurrentComposition,
        setCreateVideoOrderLoading,
        setCreateVideoOrderError,
        setVideoStep,
        setVideoOrderId,
        startPollingVideoOrder,
        setVideoUrl,
        topNineSource,
      } = props;
      const { id: templateId } = currentVideoTemplate;
      const composition = getCurrentComposition();
      setCreateVideoOrderLoading(true);
      const footages = topNineSource.posts.map(p => p.previewUri);
      return createVideoOrder({ composition, footages, templateId, stripe })
        .then(videoOrder => {
          setCreateVideoOrderLoading(false);
          setCreateVideoOrderError(false);
          if (videoOrder && videoOrder.id) {
            if (window.gtag) {
              window.gtag("event", "purchase_video");
              window.gtag("event", "purchase", {
                transaction_id: videoOrder.id,
                value: props.videoPrice,
                currency: "USD",
              });
            }
            switch (videoOrder.status) {
              case VideoStatus.PENDING:
                setVideoOrderId(videoOrder.id);
                startPollingVideoOrder();
                setVideoStep(VideoSteps.RENDERING);
                break;
              case VideoStatus.DONE:
                setVideoOrderId(videoOrder.id);
                setVideoUrl(videoOrder.videoUrl);
                setVideoStep(VideoSteps.DONE);
                break;
              default:
                console.warn("createVideoOrder result unkown status: ", videoOrder);
                break;
            }
          } else {
            console.warn("Error creating video order: ", videoOrder);
            setCreateVideoOrderError("Couldn't create video. Please try again later");
          }
          props.setLoadingPurchase(false);
        })
        .catch(err => {
          console.warn("Error creating video order: ", err);
          props.setLoadingPurchase(false);
          setCreateVideoOrderError("Couldn't create video. Please try again later");
        });
    },
    onCancelRendering: props => () => {
      const { setVideoStep, onRequestClose } = props;
      setVideoStep(VideoSteps.CHOOSE_TEMPLATE);
      onRequestClose();
    },
  }),
  withHandlers({
    handlePreviewOnClick: ({ onCreateVideoOrder, ...props }) => () => {
      props.setLoadingPurchase(true);
      return onCreateVideoOrder({});
    },
    onTokenSuccess: ({ onCreateVideoOrder, ...props }) => token => {
      props.setLoadingPurchase(true);
      return onCreateVideoOrder({
        stripe: {
          token: token.id,
        },
      });
    },
  })
);

export const withTopNineVideoModal = compose(
  withState("videoModalOpen", "setVideoModalOpen", false),
  withHandlers({
    openVideoModal: ({ setVideoModalOpen }) => () => setVideoModalOpen(true),
    closeVideoModal: ({ setVideoModalOpen }) => () => setVideoModalOpen(false),
  })
);
